import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Download, Printer, X } from 'lucide-react';
import { Botao } from '@/componentes/ui/botao';
import { ErroHttp } from '@/lib/api';
import { abrirPdfNoEdge } from '@/lib/imprimirPapel';

/**
 * O DANFE aberto numa janela por cima do raio-X, com Imprimir e Baixar.
 *
 * Baixar direto obrigava a abrir o arquivo da pasta Downloads só para olhar a
 * nota — e o mais comum é só olhar, ou imprimir. O PDF vem do mesmo endereço
 * do download com `?inline=1`, que o servidor devolve para EXIBIR em vez de
 * salvar.
 *
 * Imprimir abre o DANFE no EDGE (ver `abrirPdfNoEdge`): no checkout o Chrome
 * roda com `--kiosk-printing` e o `print()` daqui mandaria o A4 para a Zebra,
 * sem perguntar. Pelo mesmo motivo a barra do visualizador de PDF do Chrome
 * fica escondida (`#toolbar=0`) — o botão de imprimir dela faria o mesmo.
 */
export function VisualizarDanfe({
  href,
  titulo,
  aoFechar,
}: {
  /** O endereço de download (`/api/pedidos/notas/:id/pdf`). */
  href: string;
  titulo: string;
  aoFechar: () => void;
}): JSX.Element {
  const [carregado, definirCarregado] = React.useState(false);
  const [abrindo, definirAbrindo] = React.useState(false);
  const [erro, definirErro] = React.useState<string | null>(null);

  const imprimir = async (): Promise<void> => {
    definirErro(null);
    definirAbrindo(true);
    try {
      await abrirPdfNoEdge(href);
    } catch (e) {
      definirErro(e instanceof ErroHttp ? e.message : 'Não foi possível abrir o DANFE no Edge.');
    } finally {
      definirAbrindo(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(v) => !v && aoFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-slate-900/55 backdrop-blur-[2px] data-[state=open]:animate-aparecer" />
        <Dialog.Content className="fixed inset-2 z-[70] flex flex-col overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte sm:inset-6 lg:inset-x-[12vw]">
          <div className="flex items-center justify-between gap-3 border-b border-borda bg-superficie2 px-4 py-2.5">
            <Dialog.Title className="truncate text-[15px] font-bold">{titulo}</Dialog.Title>
            <Dialog.Description className="sr-only">Pré-visualização do DANFE em PDF</Dialog.Description>
            <div className="flex shrink-0 items-center gap-1.5">
              {erro ? <span className="text-[12px] text-status-divergencia">{erro}</span> : null}
              <Botao
                variante="primaria"
                tamanho="pequeno"
                onClick={() => void imprimir()}
                carregando={abrindo}
                disabled={!carregado}
                title="Abre o DANFE no Edge, com a prévia e a escolha da impressora A4."
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Botao>
              <Botao variante="secundaria" tamanho="pequeno" comoFilho>
                <a href={href} download>
                  <Download className="h-4 w-4" />
                  Baixar
                </a>
              </Botao>
              <Dialog.Close asChild>
                <Botao variante="fantasma" tamanho="iconePequeno" aria-label="Fechar (Esc)">
                  <X className="h-5 w-5" />
                </Botao>
              </Dialog.Close>
            </div>
          </div>
          <iframe
            src={`${href}?inline=1#toolbar=0`}
            title={titulo}
            onLoad={() => definirCarregado(true)}
            className="min-h-0 w-full flex-1 bg-slate-200"
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
